import React, { useContext, useState } from "react";
import "../../styles/aiPracticeCard.css";
import { WebsiteContext } from "../../context/AppContext";
import ContentComponent from "../common/ContentComponent";
import aiImage from "../../images/aiImage.png";
import { IoSend } from "react-icons/io5";
import { HiSparkles, HiLightBulb } from "react-icons/hi2";
import { FaUserGraduate } from "react-icons/fa6";
import { motion, AnimatePresence } from "framer-motion";

const practiceConversations = [
  {
    id: "corr",
    chipLabel: "Sentence Correction",
    userQuery: "Can you check this? 'I look forward to meet you tomorrow.'",
    correction: "I look forward to meeting you tomorrow.",
    highlight: "meeting",
    rule: "'Look forward to' is a phrasal verb where 'to' acts as a preposition, requiring a gerund (-ing form).",
    tip: "Alternative: 'Excited to catch up tomorrow!'",
  },
  {
    id: "email",
    chipLabel: "Polite Email Phrasing",
    userQuery: "How can I ask my boss for feedback without sounding demanding?",
    correction: "Would you have a few minutes this week to share your thoughts on the proposal?",
    highlight: "Would you have a few minutes",
    rule: "Using conditional modals ('Would you have...') softens the request while keeping it professional.",
    tip: "Alternative: 'I would welcome your guidance whenever your schedule allows.'",
  },
  {
    id: "grammar",
    chipLabel: "Affect vs Effect",
    userQuery: "What is the easy way to remember Affect vs Effect?",
    correction: "Affect is usually an Action (Verb); Effect is the End Result (Noun).",
    highlight: "Action vs End Result",
    rule: "Remember the acronym RAVEN: Remember Affect = Verb, Effect = Noun.",
    tip: "Example: The weather affected his mood; the effect was noticeable.",
  },
  {
    id: "idiom",
    chipLabel: "Everyday Idioms",
    userQuery: "Give me a natural idiom for 'doing something very rarely'.",
    correction: "'Once in a blue moon'",
    highlight: "Once in a blue moon",
    rule: "Refers to a rare phenomenon, used casually to mean something happens seldom.",
    tip: "Example: 'I only eat fast food once in a blue moon.'",
  },
];

const AiPracticeSection = () => {
  const { isMobileView, dark } = useContext(WebsiteContext);
  const [selectedConvoIndex, setSelectedConvoIndex] = useState(0);

  const activeConvo = practiceConversations[selectedConvoIndex];

  return (
    <div className={isMobileView ? "ai_practice_section_mobile" : "ai_practice_section"}>
      {/* Description on the left */}
      <div className="ai_practice_text_wrapper">
        <ContentComponent
          title={"AI Practice: Ask Any English Question Anytime"}
          content={
            "Have a tricky grammar doubt or want to sound more natural? Ask Fluent Fable's 24/7 AI tutor anything. Receive instantaneous sentence corrections, clear explanations, and polite conversational alternatives."
          }
        />
      </div>

      {/* 560px Desktop Card on the right */}
      <div className="ai_practice_card_wrapper">
        <img src={aiImage} alt="3D AI Head" className="ai_practice_3d_badge" />

        <div className={`ai_practice_card ${dark ? "ai_practice_card_dark" : "ai_practice_card_light"}`}>
          {/* Top Bar */}
          <div className="ai_tutor_top_bar">
            <div className="ai_tutor_status">
              <span className="status_dot"></span>
              <span className="ai_tutor_name">AI Speaking Tutor</span>
            </div>
            <span className="ai_mode_badge">
              <HiSparkles size={12} style={{ marginRight: 3 }} /> 24/7 Active
            </span>
          </div>

          {/* Quick Prompts Horizontal Scroll */}
          <div className="quick_prompts_scroll">
            {practiceConversations.map((item, index) => (
              <button
                key={item.id}
                className={`prompt_chip ${selectedConvoIndex === index ? "prompt_chip_active" : ""}`}
                onClick={() => setSelectedConvoIndex(index)}
              >
                {item.chipLabel}
              </button>
            ))}
          </div>

          {/* Chat Messages */}
          <div className="chat_messages_container">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeConvo.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.25 }}
                style={{ display: "flex", flexDirection: "column", gap: 12 }}
              >
                {/* User Message */}
                <div className="chat_bubble_user">
                  <div className="user_bubble_meta">
                    <FaUserGraduate size={10} /> You asked
                  </div>
                  <p>{activeConvo.userQuery}</p>
                </div>

                {/* AI Tutor Response */}
                <div className="chat_bubble_ai">
                  <div className="ai_bubble_header">
                    <HiSparkles size={14} /> AI Tutor Feedback
                  </div>

                  <div className="correction_box">
                    <strong>Suggested: </strong>
                    <span className="correction_highlight">{activeConvo.correction}</span>
                  </div>

                  <p className="explanation_text">{activeConvo.rule}</p>

                  <div className="phrasing_tip">
                    <HiLightBulb size={14} color="#f59e0b" style={{ flexShrink: 0 }} />
                    <span>{activeConvo.tip}</span>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Input Simulator */}
          <div className="ai_input_simulator">
            <span className="simulator_input_text">Ask any question or tap a prompt above...</span>
            <button className="simulator_send_btn" title="Send Question">
              <IoSend size={13} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AiPracticeSection;
